import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { InquiryType } from "types/InquiryTypes";
import {
  apiAcknowledgeInquiryStatus,
  apiCountInquiryStatusUpdates,
} from "util/network/Inquiries";

// The inquiry board itself is not on react-query (OutsourcingTable fetches
// by hand and refreshes on a flag), so this key set only covers the badge
// count. The acknowledgement guesses the count down at once and re-reads it
// afterwards; the row's marker is cleared in place by the board (see
// InquiryDialog's onStatusAck), with no refetch of the board.
/**
 * One row's marker changing: cleared when the inquiry is opened, put back if
 * the acknowledgement fails. Handed from InquiryDialog to OutsourcingTable,
 * which patches that row rather than refetching the board.
 */
export type StatusAck = { id: number; pending: boolean };

export const inquiryKeys = {
  all: ["inquiries"] as const,
  statusUpdates: () => [...inquiryKeys.all, "statusUpdates"] as const,
};

export const useInquiryStatusUpdateCount = (
  token: string,
  options?: UseQueryOptions<{ count: number }, any>
) =>
  useQuery<{ count: number }, any>(
    inquiryKeys.statusUpdates(),
    () => apiCountInquiryStatusUpdates(token),
    {
      enabled: !!token,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: true,
      ...options,
    }
  );

// Optimistic: the badge drops the moment the request goes out, and goes back
// up if it fails. The count is re-read either way, to settle what the guess
// got wrong - another inquiry acknowledged elsewhere, say.
export const useAcknowledgeInquiryStatus = (
  token: string,
  options?: Pick<
    UseMutationOptions<InquiryType, any, number>,
    "onMutate" | "onError" | "onSuccess"
  >
) => {
  const queryClient = useQueryClient();
  return useMutation<InquiryType, any, number, { count: number } | undefined>(
    (id) => apiAcknowledgeInquiryStatus(token, id),
    {
      onMutate: async (id) => {
        // An in-flight read would land after the guess and undo it.
        await queryClient.cancelQueries(inquiryKeys.statusUpdates());
        const count = queryClient.getQueryData<{ count: number }>(
          inquiryKeys.statusUpdates()
        );
        if (count) {
          queryClient.setQueryData(inquiryKeys.statusUpdates(), {
            count: Math.max(0, count.count - 1),
          });
        }
        options?.onMutate?.(id);
        return count;
      },
      onError: (error, id, count) => {
        if (count) queryClient.setQueryData(inquiryKeys.statusUpdates(), count);
        options?.onError?.(error, id, undefined);
      },
      onSuccess: (data, id) => options?.onSuccess?.(data, id, undefined),
      onSettled: () => {
        queryClient.invalidateQueries(inquiryKeys.statusUpdates());
      },
    }
  );
};
